/**
 * RecordTimeField + recordTouchpointTime (BM-H4112, Vanessa 9 Oct 2026:
 * "time recordable from everywhere").
 *
 * One optional "Record time" box for any screen where staff log a touchpoint
 * on a matter (sending an email, saving a matter note, filing post). Time goes
 * to CaseMinder's own time record through POST /time-entries, never to
 * LawBillity, and the CCL gate there still applies: a 409 comes back as a
 * plain reason, never as a silent success.
 *
 * The narrative is a standard line ("Email to ...", "Perusing letter from
 * ..."), never the content of the note or email, because it is what the
 * client reads on the invoice. The user can edit it before saving.
 */
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

export const EMPTY_TIME = { enabled: false, minutes: "", narrative: "" };

const DARK_INPUT = "w-full bg-slate-800 border border-white/10 rounded px-2 py-1 text-sm text-white";
const LIGHT_INPUT = "w-full bg-background border border-input rounded px-2 py-1 text-sm";

export function RecordTimeField({ value, onChange, defaultNarrative = "", disabled = false, testIdPrefix = "record-time", light = false }) {
  const v = value || EMPTY_TIME;
  const inputClass = light ? LIGHT_INPUT : DARK_INPUT;
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-field`}>
      <label className={`flex items-center gap-2 text-xs cursor-pointer ${light ? "" : "text-slate-300"}`}>
        <input
          type="checkbox"
          checked={v.enabled}
          disabled={disabled}
          onChange={(e) => set({ enabled: e.target.checked, narrative: v.narrative || defaultNarrative })}
          data-testid={`${testIdPrefix}-toggle`}
        />
        Record time
      </label>
      {v.enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-2">
          <input
            type="number" min="1" step="1" placeholder="Minutes" title="Recorded in 6 minute units, rounded up"
            className={inputClass}
            value={v.minutes}
            onChange={(e) => set({ minutes: e.target.value })}
            data-testid={`${testIdPrefix}-minutes`}
          />
          <input
            type="text" placeholder="Narrative for the invoice"
            className={inputClass}
            value={v.narrative}
            onChange={(e) => set({ narrative: e.target.value })}
            data-testid={`${testIdPrefix}-narrative`}
          />
        </div>
      )}
    </div>
  );
}

/** Minutes to hours in the firm's 6 minute units, rounded up (the time
 * record only accepts multiples of 0.1 hours). */
export function minutesToUnits(minutes) {
  const units = Math.max(1, Math.ceil(Number(minutes) / 6));
  return units / 10;
}

/** Problem with the time box as filled in, or "" when it can be saved. */
export function timeFieldProblem(value) {
  if (!value?.enabled) return "";
  const m = Number(value.minutes);
  if (!Number.isFinite(m) || m < 1) return "Enter the time in minutes (at least 1), or untick Record time";
  if (!(value.narrative || "").trim()) return "Enter a narrative for the time entry";
  return "";
}

/**
 * Save the time box to CaseMinder's time record. Returns
 * { recorded: false } when the box was not ticked, { recorded: true, entry }
 * on success, or { recorded: false, error } with the reason (for example the
 * CCL gate's 409). Never throws, so the touchpoint itself is never lost.
 */
export async function recordTouchpointTime({ value, matterRef, source, sourceRef = null, headers = undefined }) {
  if (!value?.enabled) return { recorded: false };
  try {
    const r = await axios.post(`${API}/time-entries`, {
      matter_ref: matterRef,
      entry_date: new Date().toISOString().slice(0, 10),
      duration_hours: minutesToUnits(value.minutes),
      narrative: value.narrative.trim(),
      source,
      source_ref: sourceRef,
    }, headers ? { headers } : undefined);
    return { recorded: true, entry: r.data };
  } catch (e) {
    return { recorded: false, error: errorMessage(e, "Time not recorded") };
  }
}
